import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: 300px;
  border: 2px solid ${({ theme }) => theme.gray4};
  border-radius: 5px;
  padding: 10px;
  margin: 15px 0 30px;
`;

const TextBtn = styled.button`
  font-size: ${({ theme }) => theme.body};
  color: ${({ theme }) => theme.text};
  outline: none;
  font-family: 'Poppins', sans-serif;
  background-color: ${({ theme, selected }) => (selected ? theme.blue : 'transparent')};
  cursor: pointer;
  border-radius: 5px;
  border: 2px solid ${({ theme, selected }) => (selected ? theme.blue : theme.gray4)};
  padding: 2px 4px;
  transition: background-color 0.05s ease-in-out, border 0.05s ease-in-out;

  :hover {
    background-color: ${({ theme, selected }) => (selected ? theme.blueHover : theme.gray4)};
    border: 2px solid ${({ theme, selected }) => (selected ? theme.blueHover : theme.gray4)};
  }
`;

const Day = styled.button`
  font-size: ${({ theme }) => theme.body};
  color: ${({ theme, isToday, selected }) => (isToday && !selected ? theme.blue : theme.text)};
  outline: none;
  font-family: 'Poppins', sans-serif;
  background-color: ${({ theme, selected }) => (selected ? theme.blue : 'transparent')};
  border-radius: 5px;
  border: 1px solid ${({ theme, isToday }) => (isToday ? theme.blue : theme.gray4)};
  padding: 2px 4px;
  transition: background-color 0.05s ease-in-out;

  :hover:not(:disabled) {
    background-color: ${({ theme, selected }) => (selected ? theme.blueHover : theme.gray4)};
    cursor: pointer;
  }
`;

const DayPlaceholder = styled.span`
  font-size: ${({ theme }) => theme.body};
  color: ${({ theme, red }) => (red ? 'rgba(255, 69, 58, .6)' : theme.textSecondary)};
  font-family: 'Poppins', sans-serif;
  padding: 2px 4px;
`;

const DaysWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-column-gap: 2px;
  grid-row-gap: 2px;
  margin: 10px 0 0;
`;

const ChangeWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  height: 100%;
  width: 100%;
  margin: 10px 0 0;
`;

const MonthChangeWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  max-height: 194px;
  width: 100%;
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 6px;
    background-color: rgb(44, 44, 46);
  }

  ::-webkit-scrollbar-thumb {
    background-color: rgb(58, 58, 60);
    border-radius: 2rem;
  }
`;

const Month = styled.button`
  font-size: ${({ theme }) => theme.body};
  color: ${({ theme }) => theme.text};
  outline: none;
  font-family: 'Poppins', sans-serif;
  background-color: ${({ theme, selected }) => (selected ? theme.blue : 'transparent')};
  border-radius: 5px;
  border: 1px solid ${({ theme, selected }) => (selected ? theme.blue : theme.gray4)};
  padding: 2px 4px;
  transition: background-color 0.05s ease-in-out;
  width: 95%;
  margin: 5px 0 0;

  :hover {
    cursor: pointer;
  }
`;

const monthsInYear = [
  'Styczeń',
  'Luty',
  'Marzec',
  'Kwiecień',
  'Maj',
  'Czerwiec',
  'Lipiec',
  'Sierpień',
  'Wrzesień',
  'Październik',
  'Listopad',
  'Grudzień',
];

function Calendar({ day, setDay, month, setMonth, year, setYear }) {
  const [isDateChange, setIsDateChange] = useState(false);
  const [days, setDays] = useState([]);

  useEffect(() => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startDay = new Date(year, month, 1).getDay();
    const today = new Date();
    const isCurrentMonth = year === today.getFullYear() && month === today.getMonth();

    if (daysInMonth < day) setDay(1);

    const arr = [];
    for (let i = 1; i <= daysInMonth + startDay - 1; i += 1) {
      arr.push(
        <Day
          type="button"
          key={i}
          empty={startDay > i}
          disabled={startDay > i}
          isToday={isCurrentMonth && today.getDate() === i - startDay + 1}
          selected={day === i - startDay + 1}
          onClick={() => setDay(i - startDay + 1)}
        >
          {startDay <= i ? i - startDay + 1 : null}
        </Day>,
      );
    }

    setDays(arr);
  }, [month, year, day, setDay]);

  return (
    <Container>
      <TextBtn
        type="button"
        onClick={() => setIsDateChange((prevState) => !prevState)}
        selected={isDateChange}
      >
        {monthsInYear[month]} {year}
      </TextBtn>
      {isDateChange ? (
        <ChangeWrapper>
          <MonthChangeWrapper>
            {monthsInYear.map((name, i) => (
              <Month type="button" key={name} selected={month === i} onClick={() => setMonth(i)}>
                {name}
              </Month>
            ))}
          </MonthChangeWrapper>
          <MonthChangeWrapper>
            {[2020, 2021, 2022, 2023, 2024, 2025].map((name) => (
              <Month
                type="button"
                key={name}
                selected={year === name}
                onClick={() => setYear(name)}
              >
                {name}
              </Month>
            ))}
          </MonthChangeWrapper>
        </ChangeWrapper>
      ) : (
        <DaysWrapper>
          <DayPlaceholder>pon</DayPlaceholder>
          <DayPlaceholder>wto</DayPlaceholder>
          <DayPlaceholder>śro</DayPlaceholder>
          <DayPlaceholder>czw</DayPlaceholder>
          <DayPlaceholder>pią</DayPlaceholder>
          <DayPlaceholder>sob</DayPlaceholder>
          <DayPlaceholder red>nie</DayPlaceholder>
          {days}
        </DaysWrapper>
      )}
    </Container>
  );
}

export default Calendar;
